/**
 * Create and initialise Tako modal
 */
/* -----------------------------------------------------------------------------
 * Tako Modal
 * -------------------------------------------------------------------------- */
import { takoUUID } from './utils/takoUUID';
import { createHtmxModal } from './htmx-components/htmx-tako-modal';
import { getLogger } from './utils/logger-context';
import { resolveComponentScope } from './utils/resolveComponentScope';

const COMPONENT_NAME = 'Modal';

const handleModalEscapeKeydown = (event) => {
    if (event.key === 'Escape') {
        const modal = document.querySelector('.tako-modal.show');

        if (modal) {
            const modalId = modal.getAttribute('id');
            toggle(modalId, false);
        }
    }
};

const validateModalId = (modalId) => {
    const logger = getLogger();

    const idSelector = modalId.charAt(0) !== '#' ? '#' : '';
    const modal = document.querySelector(idSelector + modalId);

    if (!modal) {
        logger.warn(
            `Tako Modal with id '${modalId}' was not found, please enter a valid modal id`,
        );
        return null;
    } else {
        return idSelector + modalId;
    }
};

export const toggle = (modalId, show = null) => {
    const validModalId = validateModalId(modalId);

    if (validModalId) {
        const modal = document.querySelector(validModalId);

        const allDismissButtons = modal.querySelectorAll(
            '[data-modal-dismiss]',
        );

        const enforceFocus = (event) => {
            if (
                modal.contains(event.relatedTarget) &&
                !modal.contains(event.target) &&
                !event.target.hasAttribute('data-modal-content') &&
                !event.target.closest('[data-modal-content]')
            ) {
                allDismissButtons[0].focus();
            }
        };

        const close = () => {
            modal.classList.remove('show');

            const modalButton = getModalButton(modal);

            if (modalButton) {
                modalButton.focus();
            }

            document.body.classList.remove('tako-modal--open');
            document.body.removeEventListener(
                'keydown',
                handleModalEscapeKeydown,
            );
            document.removeEventListener('focusin', enforceFocus);

            // Emit a custom event when the modal is closed
            const closeEvent = new CustomEvent('takoModalClose', {
                detail: {
                    modal,
                    id: modal.id,
                },
            });
            modal.dispatchEvent(closeEvent);
        };

        const open = () => {
            modal.classList.add('show');
            modal.setAttribute('tabindex', '-1');

            document.body.classList.add('tako-modal--open');
            document.body.addEventListener('keydown', handleModalEscapeKeydown);
            document.addEventListener('focusin', enforceFocus);

            // Emit a custom event when the modal is opened
            const openEvent = new CustomEvent('takoModalOpen', {
                detail: {
                    modal,
                    id: modal.id,
                },
            });
            modal.dispatchEvent(openEvent);
        };

        const shouldOpen =
            show !== null ? show : !modal.classList.contains('show'); // If `open` is null, toggle based on current state

        if (shouldOpen) {
            open();
        } else {
            close();
        }
    }
};

export const init = (unresolvedScope) => {
    const logger = getLogger();
    const scope = resolveComponentScope(unresolvedScope);
    const defaultLoggerArgs = {
        componentName: COMPONENT_NAME,
        scope,
    };
    logger.debug(`Initialising ${COMPONENT_NAME} component.`, { scope });

    const allHtmxModalButtons = scope.querySelectorAll('[data-modal-hx-url]');

    if (allHtmxModalButtons) {
        allHtmxModalButtons.forEach((htmxModalButton) => {
            createHtmxModal(htmxModalButton);
        });
    }

    const allTakoModals = scope.querySelectorAll('.tako-modal');

    allTakoModals.forEach((takoModal) => {
        try {
            const instanceLoggerArgs = {
                ...defaultLoggerArgs,
                element: takoModal,
            };
            takoModal.setAttribute('aria-modal', true);
            takoModal.setAttribute('role', 'dialog');

            const dismissButton = createDismissButton();
            const modalDialog = takoModal.querySelector('.tako-modal__dialog');

            // Check the modal contains a dialog before appending the dismiss button
            if (!modalDialog) {
                logger.warn(
                    'Tako modals must contain a dialog - add one using the `tako-modal__dialog` class',
                    instanceLoggerArgs,
                );
                return;
            }

            modalDialog.setAttribute('role', 'document');
            modalDialog.prepend(dismissButton);

            const takoModalId = takoModal.getAttribute('id');

            if (!takoModalId) {
                logger.warn(
                    'Tako modals must have a unique id - add one using the `id="modalId"` attribute',
                    instanceLoggerArgs,
                );
                return;
            }

            const takoModalButton = getModalButton(takoModal);

            if (takoModalButton) {
                takoModalButton.addEventListener('click', () => {
                    toggle(takoModalId, true);
                });
            }

            if (takoModal.getAttribute('data-overlay') !== 'static') {
                takoModal.addEventListener('click', (event) => {
                    if (event.target === takoModal) {
                        toggle(takoModalId, false);
                    }
                });
            }

            if (takoModalButton) {
                const modalTitle =
                    takoModalButton.getAttribute('data-modal-title');

                if (modalTitle) {
                    addModalHeader(takoModal, modalTitle);
                }
            }

            const modalHeader = takoModal.querySelector('.tako-modal__title');

            if (modalHeader) {
                const modalTitleId = takoUUID('modal-title');
                modalHeader.setAttribute('id', modalTitleId);
                takoModal.setAttribute('aria-labelledby', modalTitleId);
            }

            const isAutoDismissButton =
                takoModal.hasAttribute('data-auto-dismiss-button') ||
                (takoModalButton &&
                    takoModalButton.hasAttribute(
                        'data-modal-auto-dismiss-button',
                    ));

            // Add footer and dismiss button automaticaly with data-auto-dismiss-button="buttonLabel"
            if (isAutoDismissButton) {
                addAutoDismissButton(takoModal);
            }

            const allDismissButtons = takoModal.querySelectorAll(
                '[data-modal-dismiss]',
            );
            allDismissButtons.forEach((dismissButton) => {
                const dismissAttribute =
                    dismissButton.getAttribute('data-modal-dismiss');

                if (dismissAttribute === 'true' || dismissAttribute === '') {
                    dismissButton.addEventListener('click', () =>
                        toggle(takoModalId, false),
                    );
                }
            });

            const focusableElements = takoModal.querySelectorAll(
                'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])',
            );
            const focusLastElement = (event) => {
                if (event.shiftKey && event.key === 'Tab') {
                    event.preventDefault();
                    focusableElements[focusableElements.length - 1]?.focus();
                }
            };

            if (focusableElements[0]) {
                focusableElements[0].addEventListener(
                    'keydown',
                    focusLastElement,
                );
            }
        } catch (error) {
            logger.error(error, {
                ...defaultLoggerArgs,
                element: takoModal,
            });
        }
    });
};

const getModalButton = (modal) => {
    const modalId = modal.getAttribute('id');
    const modalButton = document.querySelector(
        `[data-modal-target='${modalId}'],
        [data-modal-target='#${modalId}']`,
    );

    return modalButton;
};

const addModalHeader = (modal, title) => {
    const modalHeader = document.createElement('h2');
    modalHeader.innerText = title;
    modalHeader.classList.add('tako-modal__title');

    const modalDialog = modal.querySelector('.tako-modal__dialog');
    modalDialog.prepend(modalHeader);
};

const createDismissButton = (label) => {
    const dismissButton = document.createElement('button');
    dismissButton.setAttribute('data-modal-dismiss', true);
    dismissButton.setAttribute('type', 'button');

    if (label) {
        dismissButton.classList.add('tako-button', 'tako-button--secondary');
        dismissButton.innerText = label;
    } else {
        dismissButton.classList.add(
            'tako-button',
            'tako-button--secondary',
            'tako-button--ghost',
            'tako-button--icon',
        );
        dismissButton.innerHTML = `<i class="fas fa-xmark"></i>`;
        dismissButton.setAttribute('aria-label', 'Click to close modal');
    }

    return dismissButton;
};

const addAutoDismissButton = (modal) => {
    let takoModalFooter = modal.querySelector('.tako-modal__footer');
    const modalDialog = modal.querySelector('.tako-modal__dialog');
    const modalButton = getModalButton(modal);
    const autoDismissButtonLabel =
        modal.getAttribute('data-auto-dismiss-button') ||
        modalButton.getAttribute('data-modal-auto-dismiss-button');
    const autoDismissButton = createDismissButton(autoDismissButtonLabel);

    // Add dismiss button to existing modal footer
    if (takoModalFooter) {
        const footerDismissButton = takoModalFooter.querySelector(
            '[data-modal-dismiss]',
        );
        let buttonGroup = takoModalFooter.querySelector('.tako-button-group');

        if (!footerDismissButton) {
            if (buttonGroup) {
                buttonGroup.prepend(autoDismissButton);
            } else {
                buttonGroup = document.createElement('div');
                buttonGroup.classList.add('tako-button-group');
                if (takoModalFooter.childNodes.length > 0) {
                    buttonGroup.prepend(...takoModalFooter.childNodes);
                }
                buttonGroup.prepend(autoDismissButton);
                takoModalFooter.appendChild(buttonGroup);
            }
        } else {
            footerDismissButton.innerText = autoDismissButtonLabel;
        }
    } else {
        // Create modal footer and add dismiss button to footer
        takoModalFooter = document.createElement('div');
        takoModalFooter.classList.add('tako-modal__footer');
        takoModalFooter.prepend(autoDismissButton);
        modalDialog.appendChild(takoModalFooter);
    }
};
